import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@services/notification.service';

import { AuthService } from '@services/auth.service';
import { SnackBarService } from '@services/snack-bar.service';
import { DialogContextService } from '@services/dialog-context.service';
import { RATE_LIMIT_STATUS } from '@helper/rate-limit.helper';
import { GCC_DOCUMENTS_PATH } from '@core/constants/guided-course-creation.constants';
import { SEARCH_API_PATH } from '@modules/search/search.constants';

// A rejected QR scan is an expected outcome that the scanner dialog already
// reports inline: wrong day, not a participant, invalid code, or a scanner who
// isn't a trainer of the event. It must not reach the generic handling below —
// the platform renders every authorization denial as HTTP 401 (the body carries
// `code: '403'`), which would sign the user out mid-scan, and the error dialog
// would sit hidden behind the full-screen scanner until they navigate back.
const ATTENDANCE_SCAN_PATH = 'attendances/scan';

// Password reset flows report their own failures.
const PASSWORD_RESET_PATH = 'password_resets';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private translateService: TranslateService,
    private snackbarService: SnackBarService,
    private notify: NotificationService,
    private dialogContextService: DialogContextService,
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      tap(
        () => {},
        (errorResponse: unknown) => {
          if (
            !(errorResponse instanceof HttpErrorResponse) ||
            errorResponse.status === 0
          ) {
            return;
          }

          if (request.url.includes(ATTENDANCE_SCAN_PATH)) {
            return;
          }

          if (request.url.includes(SEARCH_API_PATH)) {
            return;
          }

          // Rate limits are reported inline by the page that made the request,
          // so the generic dialog would stack on top of that message.
          if (errorResponse.status === RATE_LIMIT_STATUS) {
            return;
          }

          if (
            request.url.indexOf('token_refresh') === -1 &&
            errorResponse.status === 401
          ) {
            this.authService.logout(undefined, {
              return: location.href,
              sessionExpired: true,
            });
          } else if (
            request.url.indexOf('users/login') === -1 &&
            request.url.indexOf('members/personio_import') === -1 &&
            request.url.indexOf('members/custom_attributes') === -1 &&
            request.url.indexOf('users/resend_invitation') === -1 &&
            request.url.indexOf('h5p-service/content-creator') === -1 &&
            request.url.indexOf('outline/module_content') === -1 &&
            request.url.indexOf(GCC_DOCUMENTS_PATH) === -1 &&
            request.url.indexOf(PASSWORD_RESET_PATH) === -1 &&
            !request.url.endsWith('ilt_event_assignments')
          ) {
            if (errorResponse.status === 401) {
              this.snackbarService.error(
                `${this.translateService.instant(
                  'notification.error.e401.title',
                )}
                ${this.translateService.instant(
                  'notification.error.e401.message',
                )}`,
              );
            } else {
              // Don't show error dialog if ILT event dialog, cohort calendar, or cohort profile is open
              if (
                !this.dialogContextService.isIltEventDialogOpen() &&
                !this.dialogContextService.isCohortCalendarOpen() &&
                !this.dialogContextService.isCohortProfileOpen()
              ) {
                this.notify.handleError(errorResponse);
              }
            }
          }
        },
      ),
    );
  }
}
